import { useQueryClient } from '@tanstack/react-query';
import type { CurrentUserDto } from '@cpm/shared';
import { ShieldCheck } from 'lucide-react';
import { useLogout } from '../api/auth.js';
import { TwoFactorEnrolFlow } from './account-security.js';
import { Card } from './ui.js';

/**
 * Full-screen gate shown when the logged-in user's role requires 2FA but they
 * have not enrolled (G17). The API enforces the same rule with 403
 * TWO_FACTOR_ENROLL_REQUIRED on every other route, so there is no way past
 * this screen but to enrol (or sign out). The gate lifts only after the user
 * acknowledges their recovery codes, which refreshes identity.
 */
export function ForcedTwoFactorEnrolPage({ user }: { user: CurrentUserDto }) {
  const queryClient = useQueryClient();
  const logout = useLogout();

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-xl bg-brand-600 text-white">
            <ShieldCheck className="size-6" />
          </div>
          <h1 className="text-lg font-semibold">Set up two-factor authentication</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Two-factor is required for your role. Set it up to continue as {user.email}.
          </p>
        </div>
        <Card className="p-6">
          <TwoFactorEnrolFlow
            onDone={() => queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })}
          />
        </Card>
        <button
          type="button"
          onClick={() => logout.mutate()}
          className="mt-4 w-full text-center text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
